import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import { PhoneWithCopyIcon } from "~ui/molecules/PhoneWithCopyIcon/PhoneWithCopyIcon";
import { makeProfileAvatarUrl } from "~utils/url";

import {
  participantDisplayName,
  participantInitials,
  type ParticipantRow,
  STATUS_OPTIONS,
  statusLabelForKey,
} from "./emergencyCommandCenterShared";

export interface EmergencyParticipantStatusDialogProps {
  open: boolean;
  onClose: () => void;
  participant: ParticipantRow | null;
  /** Map locations for optional location select (active only). */
  mapLocations: { id: string; isActive?: boolean; label: string }[];
  mapLocationsLoading?: boolean;
  onSave: (payload: { bubbleMapLocationId: string | null; statusKey: string }) => Promise<void>;
  saving?: boolean;
  errorMessage?: string | null;
  /** Shown next to the title (e.g. "You" chip is handled by parent if needed). */
  title?: string;
}

export function EmergencyParticipantStatusDialog({
  errorMessage,
  mapLocations,
  mapLocationsLoading = false,
  onClose,
  onSave,
  open,
  participant,
  saving = false,
  title = "Update status",
}: EmergencyParticipantStatusDialogProps) {
  const [statusKey, setStatusKey] = useState<string>("not-reported");
  const [mapLocationId, setMapLocationId] = useState<string>("");

  useEffect(() => {
    if (open && participant) {
      setStatusKey(participant.statusKey ?? "not-reported");
      setMapLocationId(participant.bubbleMapLocationId ?? "");
    }
  }, [open, participant]);

  const handleSave = useCallback(async () => {
    if (!participant?.userId) {
      return;
    }
    await onSave({
      bubbleMapLocationId: mapLocationId.trim() ? mapLocationId : null,
      statusKey,
    });
  }, [mapLocationId, onSave, participant?.userId, statusKey]);

  const p = participant;
  const roles = (p?.bubbleRoles ?? []).filter(Boolean);

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {p ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack alignItems="center" direction="column" spacing={1}>
              <Avatar alt="" src={p.userId ? makeProfileAvatarUrl(p.userId) : undefined} sx={{ height: 88, width: 88 }}>
                {participantInitials(p)}
              </Avatar>
              <Typography textAlign="center" variant="subtitle1">
                {participantDisplayName(p)}
              </Typography>
              {p.phoneNumber?.trim() ? (
                <PhoneWithCopyIcon phoneNumber={p.phoneNumber.trim()} showPhoneIcon={false} />
              ) : (
                <Typography color="text.secondary" variant="body2">
                  —
                </Typography>
              )}
              <Typography color="text.secondary" variant="body2">
                <strong>Role:</strong> {roles.length > 0 ? roles.join(", ") : "—"}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                <strong>Current status:</strong> {statusLabelForKey(p.statusKey)}
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <InputLabel id="emergency-dialog-status-label">Status</InputLabel>
              <Select
                disabled={saving}
                label="Status"
                labelId="emergency-dialog-status-label"
                onChange={(e) => setStatusKey(String(e.target.value))}
                value={statusKey}
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="emergency-dialog-loc-label">Map location (optional)</InputLabel>
              <Select
                disabled={saving || mapLocationsLoading}
                label="Map location (optional)"
                labelId="emergency-dialog-loc-label"
                onChange={(e) => setMapLocationId(String(e.target.value))}
                value={mapLocationId}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {mapLocations
                  .filter((l) => l.isActive !== false)
                  .map((l) => (
                    <MenuItem key={l.id} value={l.id}>
                      {l.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {errorMessage ? (
              <Typography color="error" variant="body2">
                {errorMessage}
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ pb: 2, px: 3 }}>
        <Button disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={saving || !p?.userId || mapLocationsLoading}
          onClick={() => void handleSave()}
          startIcon={saving ? <CircularProgress color="inherit" size={16} /> : null}
          variant="contained"
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
